import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router";
import moment from "moment";
import {
  Button,
  Dialog,
  Divider,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  SettingsOutlined as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ArrowLeft,
  ArrowRight,
} from "@mui/icons-material";

import {
  getCurrentYear,
  getDefaultInflationRate,
  MONTH_LIST,
  MONTH_NUM_MAP,
} from "../../../common/shared";
import { DARK_THEME } from "../../../redux/constants";
import VestivoLogo from "../../../assets/engage-full-color.svg";
import VestivoLogoDarkTheme from "../../../assets/engage-full-white.svg";
import {
  createPlan,
  saveExpenseList,
  savePlanIncome,
  savePlanLifeInsurance,
  savePlanTaxes,
  savePlanAssets,
  getPlanElements,
  deleteExpenseList,
  deletePlanIncome,
  deleteAssetList,
  deletePlanInsurancePolicy,
  evaluateFilingStatus,
  saveTaxFilingChangesUponDeath,
  getDefaultTaxCredits,
  evaluateEarlyDistribution,
  updatePlanName,
  evaluateSSSurvivorBenefits,
  saveSSSurvivorBenefits,
} from "../../../api/advisor/plans";
import { getAdvisorTaxDeductions } from "../../../api/advisor/taxes";
import { UPDATE_PLAN_CREATION_ID } from "../../../redux/constants";
import { useDispatch } from "react-redux";
import { setAssetAccountType } from "../../../common/shared";
import {
  Tabs,
  ExpensesTab,
  IncomesTab,
  AssetsTab,
  InsuranceTab,
  TaxesTab,
} from "./components";
import TaxFilingStatusUponDeathStep from "./components/TaxFilingStatusUponDeathStep";
import SocialSecuritySurvivorBenefits from "./components/SocialSecuritySurvivorBenefits";
import PlanNameTab from "./components/PlanNameTab";
import {
  checkDateRangeValid,
  createTaxCreditObject,
  getDefaultLifeExpectancyDate,
  getStateAbbreviation,
  makeDefaultExpenses,
  makeDefaultIncomes,
  makeExpensesPayload,
  makeIncomePayload,
  makeInsurancePayload,
  getLifeExpectancyDate,
  makeAssetsPayload,
} from "./utils";

import "./PlanCreationModal.scss";
import ConfirmationDialog from "@instream-solutions/instream-design-system/lib/UX/ConfirmationDialog/ConfirmationDialog";
import Loader from "../../Utility/Loader/Loader";
import { RMD_ACCOUNT_TYPES } from "../../../api/advisor/accounts";
import AssetDistributionWarningModal from "../AssetDistributionWarningModal/AssetDistributionWarningModal";
import { useTranslation } from "react-i18next";

const EXPENSES_STEP = 0;
const INCOMES_STEP = 1;
const ASSETS_STEP = 2;
const TAXES_STEP = 4;
const TAX_FILING_STATUS_STEP = 5;
const PLAN_NAME_STEP = 6;
const SOCIAL_SECURITY_BENEFITS_STEP = 10;

const currYear = new Date().getFullYear();

const PlanCreationModal = ({
  onClose,
  currentClientState,
  defaultInflation,
  displayMode,
  onSavePlan,
  ...props
}) => {
  const [state, setState] = useState({
    isNew: true,
    openCancelCreatePlanModal: false,
    hasRMD: false,
    needDistributionSort: false,
  });
  const [isPageChanging, setIsPageChanging] = useState(false);
  const [tab, setTab] = useState(0);
  const [isUserDetailsVisible, setIsUserDetailsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [planId, setPlanId] = useState(null);
  const [expenses, setExpenses] = useState(
    makeDefaultExpenses(
      currentClientState.get("client").toJS(),
      currentClientState.get("coClient").toJS()
    )
  );
  const [incomes, setIncomes] = useState(
    makeDefaultIncomes(
      currentClientState.get("client").toJS(),
      currentClientState.get("coClient").toJS()
    )
  );

  // placeholder to allow completion of https://instreamsolutions.atlassian.net/browse/INP-2422
  // and so https://instreamsolutions.atlassian.net/browse/INP-2205 can be worked on when the time comes.
  const [assets, setAssets] = useState([]);

  const [insurances, setInsurances] = useState([]);

  const [planName, setPlanName] = useState("");

  const [showNoAssetsMessage, setShowNoAssetsMessage] = useState(false);

  const [distributionWarning, setDistributionWarning] = useState({
    earlyDistribution: false,
    earlyDistributionYear: null,
    earlyNoPenaltyAssetDistribution: false,
    earlyNoPenaltyAssetDistributionYear: null,
  });

  const { t } = useTranslation();

  const currentUser = props.currentUser.toJS();

  // client details
  const client = useMemo(() => currentClientState.get("client").toJS(), [
    currentClientState,
  ]);
  const coClient = useMemo(() => currentClientState.get("coClient").toJS(), [
    currentClientState,
  ]);

  const lifeExpectancyYear = getDefaultLifeExpectancyDate(
    null,
    client,
    coClient
  );

  const clientState = useMemo(() => {
    const state =
      typeof client.addressList[0].state === "string"
        ? client.addressList[0].state
        : client.addressList[0].state.name;
    return state;
  });

  const [taxDeductions, setTaxDeductions] = useState({});

  const [taxes, setTaxes] = useState({
    taxFilingStatusType:
      coClient.id !== undefined ? "MARRIED_FILING_JOINTLY" : "SINGLE",
    deductionType: "STANDARD",
    itemizedDeductionAmount: 0,
    retirementStateAbbreviation: getStateAbbreviation(clientState),
    planWizardTaxCreditViewList: [
      {
        setPlanWizardTaxCreditId: null,
        amount: 0,
        date: {
          startDateType: "REGULAR_DATE",
          endDateType: "REGULAR_DATE",
          relativeStartDateNum: 0,
          relativeEndDateNum: 0,
          startYear: currYear,
          endYear: currYear,
          startMonth: 1,
          endMonth: 12,
        },
      },
    ],
    planWizardCapitalGainsViewList: [
      {
        taxPeriodId: null,
        amount: 0,
        date: {
          startDateType: "REGULAR_DATE",
          endDateType: "REGULAR_DATE",
          relativeStartDateNum: 0,
          relativeEndDateNum: 0,
          startYear: currYear,
          endYear: currYear,
          startMonth: 1,
          endMonth: 12,
        },
      },
    ],
    planPropertyTaxViewList: [
      {
        propertyTaxId: null,
        amount: 0,
        date: {
          startDateType: "REGULAR_DATE",
          endDateType: "REGULAR_DATE",
          relativeStartDateNum: 0,
          relativeEndDateNum: 0,
          startYear: currYear,
          endYear: lifeExpectancyYear.getFullYear(),
          startMonth: new Date().getMonth() + 1,
          endMonth: lifeExpectancyYear.getMonth() + 1,
        },
      },
    ],
  });

  const [taxFilingUponDeath, setTaxFilingUponDeath] = useState({
    id: null,
    taxFillingStatusType: "HEAD_OF_HOUSEHOLD",
    deductionType: "STANDARD",
    itemizedDeduction: 0,
  });

  const [defaultTaxCredits, setDefaultTaxCredits] = useState([]);

  const [clientDeathYear, setClientDeathYear] = useState(getCurrentYear());

  const [
    socialSecuritySurvivorBenefits,
    setSocialSecuritySurvivorBenefits,
  ] = useState({});

  const [
    hasSocialSecuritySurvivorBenefits,
    setHasSocialSecuritySurvivorBenefits,
  ] = useState(false);

  const isTaxFilingItemizedDeductionValid = () => {
    if (taxFilingUponDeath.deductionType === "STANDARD") return true;

    const taxFilingStatusData = taxDeductions.find(
      (item) =>
        item.taxFilingStatusKey === taxFilingUponDeath.taxFillingStatusType
    );
    return (
      taxFilingUponDeath.itemizedDeduction >=
      taxFilingStatusData.itemizedDeduction
    );
  };

  const history = useHistory();

  // inflation rate
  const inflationRate = useMemo(
    () => (getDefaultInflationRate(defaultInflation.toJS()) * 100).toFixed(1),
    [defaultInflation]
  );

  const clientAge = useMemo(
    () => moment().diff(moment(client.dateOfBirth), "years"),
    [client.dateOfBirth]
  );
  const clientPhone = useMemo(
    () => (client.phoneList.length ? client.phoneList[0].phoneNumber : "-"),
    [client.phoneList]
  );
  const clientEmail = useMemo(
    () => (client.emailList.length ? client.emailList[0].emailAddress : "-"),
    [client.emailList]
  );
  const clientAddress = useMemo(() => {
    if (client.addressList.length === 0) return "-";
    const addressLine = client.addressList[0].addressLine;
    const state =
      typeof client.addressList[0].state === "string"
        ? client.addressList[0].state
        : client.addressList[0].state.name;
    return addressLine === "" ? "-" : addressLine + ", " + state;
  }, [client.addressList]);

  // dark theme
  const isDarkTheme = useMemo(() => displayMode === DARK_THEME, [displayMode]);

  const advisorId = currentUser.advisor.advisorId;
  const clientId = client.id;

  const dispatch = useDispatch();

  useEffect(() => {
    const initializePlan = () => {
      const today = new Date();
      const dd = String(today.getDate()).padStart(2, "0");
      const mm = String(today.getMonth() + 1).padStart(2, "0"); //January is 0!
      const yyyy = today.getFullYear();
      const newPlanName = `Plan For ${client.firstName} ${client.lastName} ${yyyy}/${mm}/${dd}`;

      createPlan(advisorId, clientId, newPlanName).then((response) => {
        setPlanId(response.planId);
        dispatch({
          type: UPDATE_PLAN_CREATION_ID,
          value: response.planId,
        });
        const expensePromise = saveExpenseList(
          advisorId,
          clientId,
          response.planId,
          makeExpensesPayload(expenses, inflationRate)
        );
        const incomePromise = savePlanIncome(
          advisorId,
          clientId,
          response.planId,
          makeIncomePayload(incomes, inflationRate)
        );

        Promise.all([expensePromise, incomePromise]).then((data) => {
          setExpenses((v) =>
            data[0].map((item, index) => {
              return {
                id: item.id,
                name: item.name,
                checked: false,
                owner: getItemOwner(item.appliesTo.id),
                amount: item.amount,
                startYear: item.startYear,
                startMonth: item.startMonth,
                endYear: item.endYear,
                endMonth: item.endMonth,
                isEdited: false,
              };
            })
          );

          setIncomes((v) =>
            data[1].map((item) => {
              return {
                id: item.incomeId,
                name: item.name,
                checked: false,
                owner: getItemOwner(item.appliesTo.id),
                amount: item.amount,
                startYear: item.startYear,
                startMonth: item.startMonth,
                endYear: item.endYear,
                endMonth: item.endMonth,
                isEdited: false,
                incomeType: {
                  key: item.incomeType.key,
                },
              };
            })
          );

          setIsLoading(false);
        });
      });
    };

    const loadExistingPlan = () => {
      getPlanElements(advisorId, clientId, props.planCreationId).then(
        (elements) => {
          setPlanId(props.planCreationId);

          setExpenses((v) =>
            elements.expenses.map((item, index) => {
              return {
                id: item.id,
                name: item.name,
                checked: false,
                owner: getItemOwner(item.appliesTo.id),
                amount: item.amount,
                startYear: item.startYear,
                startMonth: item.startMonth,
                endYear: item.endYear,
                endMonth: item.endMonth,
                isEdited: false,
              };
            })
          );

          setIncomes((v) =>
            elements.incomes.map((item, index) => {
              return {
                id: item.incomeId,
                name: item.name,
                checked: false,
                owner: getItemOwner(item.appliesTo.id),
                amount: item.amount,
                startYear: item.startYear,
                startMonth: item.startMonth,
                endYear: item.endYear,
                endMonth: item.endMonth,
                isEdited: false,
                incomeType: {
                  key: item.incomeType.key,
                },
              };
            })
          );

          setAssets((v) =>
            elements.accounts
              .map((item) => {
                return {
                  ...item,
                  accountType: setAssetAccountType(t, item.accountType.key),
                  contributions:
                    item.contributions.length > 0
                      ? item.contributions
                      : [
                          {
                            id: null,
                            contributionType: {
                              key: "AMOUNT",
                            },
                            amount: 0,
                            percentage: 0,
                            frequencyType: {
                              key: "MONTHLY",
                            },
                            startYear: getCurrentYear(),
                            endYear: getCurrentYear(),
                            startMonth: 1,
                            endMonth: 1,
                          },
                        ],
                };
              })
              .map((item, index) => {
                return {
                  ...item,
                  preRetirementAllocation: item.preRetirementAllocation * 100,
                  postRetirementAllocation: item.postRetirementAllocation * 100,
                  contributionType:
                    item.contributions[0].contributionType.key === "AMOUNT"
                      ? "monthly"
                      : "percent",
                  monthlyContribution: item.contributions[0].amount,
                  percentContribution: item.contributions[0].percentage,
                  frequencyType: item.contributions[0].frequencyType.key,
                  startYear: item.contributions[0].startYear,
                  endYear: item.contributions[0].endYear,
                  startMonth: MONTH_NUM_MAP[item.contributions[0].startMonth],
                  endMonth: MONTH_NUM_MAP[item.contributions[0].endMonth],
                };
              })
          );

          setInsurances((v) =>
            elements.insurancePolicies.map((item, index) => {
              return {
                id: item.id,
                editIndex: index,
                name: item.name,
                clientId: item.clientId,
                appliesTo: item.appliesTo.id,
                scopeType: item.scopeType,
                policyType: item.policyType.key,
                endDateValid: true,
                deathBenefit: item.deathBenefit,
                premium: item.premium,
                cashValue: item.cashValue,
                policyEndDate: item.policyEndDate,
                isEdited: false,
                isCopied: false,
                inEdit: false,
                isNew: false,
              };
            })
          );

          setIsLoading(false);
        }
      );
    };

    const advisorTaxDeductionPromise = getAdvisorTaxDeductions(advisorId);
    const defaultTaxCreditsPromise = getDefaultTaxCredits(advisorId);

    Promise.all([advisorTaxDeductionPromise, defaultTaxCreditsPromise]).then(
      (data) => {
        setTaxDeductions(data[0]);
        setDefaultTaxCredits(createTaxCreditObject(data[1]));

        if (props.planCreationId === null) {
          initializePlan();
          return;
        }

        loadExistingPlan();
      }
    );
  }, []);

  const goToPlanSettings = () => {
    onClose();
    history.push("/admin/settings/plan-settings/default-settings");
  };

  const toggleUserDetails = () => {
    setIsUserDetailsVisible((prevState) => !prevState);
  };

  const getItemOwner = (ownerId) => {
    if (ownerId === 0) return { id: 0, firstName: "Household", lastName: "" };
    return ownerId === client.id ? client : coClient;
  };

  const saveExpenses = async () => {
    const collection = makeExpensesPayload(expenses, inflationRate);

    if (collection.length === 0) return;

    const results = await saveExpenseList(
      advisorId,
      clientId,
      planId,
      collection
    );

    setExpenses((v) =>
      results.map((item, index) => {
        return {
          id: item.id,
          name: item.name,
          checked: false,
          owner: getItemOwner(item.appliesTo.id),
          amount: item.amount,
          startYear: item.startYear,
          startMonth: item.startMonth,
          endYear: item.endYear,
          endMonth: item.endMonth,
          isEdited: false,
        };
      })
    );

    setState({
      ...state,
      isNew: true,
      openCancelCreatePlanModal: false,
    });
  };

  const saveInsurances = async () => {
    if (!checkInsurancesValid(insurances)) {
      setIsPageChanging(false);
      return false;
    }
    const collection = makeInsurancePayload(insurances, clientId);
    const results = await savePlanLifeInsurance(
      advisorId,
      clientId,
      planId,
      collection
    );
    setInsurances((v) =>
      results.map((item, index) => {
        return {
          id: item.id,
          editIndex: index,
          name: item.name,
          clientId: item.clientId,
          appliesTo: item.appliesTo.id,
          scopeType: item.scopeType,
          policyType: item.policyType.key,
          endDateValid: true,
          deathBenefit: item.deathBenefit,
          premium: item.premium,
          cashValue: item.cashValue,
          policyEndDate: item.policyEndDate,
          isEdited: false,
          isCopied: false,
          inEdit: false,
          isNew: false,
        };
      })
    );
    setState({
      ...state,
      isNew: true,
      openCancelCreatePlanModal: false,
    });
    return true;
  };

  const saveIncomes = async () => {
    if (!checkIncomesValid(incomes)) {
      setIsPageChanging(false);
      return false;
    }
    const collection = makeIncomePayload(incomes, inflationRate);

    const results = await savePlanIncome(
      advisorId,
      clientId,
      planId,
      collection
    );
    const newIncomes = results.map((item, index) => {
      return {
        id: item.incomeId,
        name: item.name,
        checked: false,
        owner: getItemOwner(item.appliesTo.id),
        amount: item.amount,
        startYear: item.startYear,
        startMonth: item.startMonth,
        endYear: item.endYear,
        endMonth: item.endMonth,
        isEdited: false,
        incomeType: {
          key: item.incomeType.key,
        },
      };
    });
    setIncomes(newIncomes);
    setState({
      ...state,
      isNew: true,
      openCancelCreatePlanModal: false,
    });
    return true;
  };

  const isTaxFilingStatusChangingOnDeath = async () => {
    return await evaluateFilingStatus(
      advisorId,
      clientId,
      planId,
      taxes.taxFilingStatusType
    );
  };

  const updateTaxFilingStatusUponDeath = async () => {
    return await saveTaxFilingChangesUponDeath(advisorId, clientId, planId, {
      id: null,
      taxFillingStatusType: taxFilingUponDeath.taxFillingStatusType,
      deductionType: taxFilingUponDeath.deductionType,
      itemizedDeduction: taxFilingUponDeath.itemizedDeduction,
    });
  };

  const saveTaxes = async () => {
    if (!checkTaxesValid(taxes)) {
      setIsPageChanging(false);
      return false;
    }

    const taxResponse = await savePlanTaxes(advisorId, clientId, planId, {
      ...taxes,
      planId,
    });

    const t = taxes;
    t.planWizardCapitalGainsViewList[0].taxPeriodId =
      taxResponse.data.payload.planWizardCapitalGainsViewList[0].taxPeriodId;
    t.planPropertyTaxViewList[0].propertyTaxId =
      taxResponse.data.payload.planPropertyTaxViewList[0].propertyTaxId;

    setTaxes(t);

    setState({
      ...state,
      isNew: true,
      openCancelCreatePlanModal: false,
    });
    return true;
  };

  const saveAssets = async () => {
    const results = await savePlanAssets(
      advisorId,
      clientId,
      planId,
      makeAssetsPayload(assets)
    );

    setAssets(
      results.map((item, index) => {
        return {
          ...assets[index],
          id: item.id,
        };
      })
    );

    setState({
      ...state,
      isNew: true,
      openCancelCreatePlanModal: false,
      hasRMD: false,
      needDistributionSort: false,
    });
  };

  const savePlanName = async () => {
    const results = await updatePlanName(advisorId, clientId, planId, planName);
    return results;
  };

  const shouldShowSurvivorBenefits = async () => {
    const survivorBenefitsResult = await evaluateSSSurvivorBenefits(
      advisorId,
      clientId,
      planId
    );
    setSocialSecuritySurvivorBenefits({
      ...survivorBenefitsResult,
      noSssBenefitEndYear: true,
      survivorBenefitEndYear: null,
      defaultStartYear: survivorBenefitsResult.survivorBenefitStartYear,
      defaultEndYear: survivorBenefitsResult.survivorBenefitEndYear,
    });
    return survivorBenefitsResult.eligibleForSSISurvivorBenefit;
  };

  const goToNextStep = async () => {
    if (isPageChanging) return;
    setIsPageChanging(true);

    if (tab === 0) {
      const valid = checkExpensesValid(expenses);
      if (!valid) {
        setIsPageChanging(false);
        return;
      }
      await saveExpenses();
    }

    if (tab === INCOMES_STEP) {
      if (!(await saveIncomes())) return;

      if (await shouldShowSurvivorBenefits()) {
        // temp for testing
        setIsPageChanging(false);
        setTab(SOCIAL_SECURITY_BENEFITS_STEP);
        return;
      }
    }

    if (tab === SOCIAL_SECURITY_BENEFITS_STEP) {
      if (!hasSocialSecuritySurvivorBenefits) {
        setIsPageChanging(false);
        setTab(ASSETS_STEP);
        return;
      }

      await saveSSSurvivorBenefits(
        advisorId,
        clientId,
        planId,
        socialSecuritySurvivorBenefits
      );
      const elements = await getPlanElements(advisorId, clientId, planId);
      setIncomes((v) =>
        elements.incomes.map((item, index) => {
          return {
            id: item.incomeId,
            name: item.name,
            checked: false,
            owner: getItemOwner(item.appliesTo.id),
            amount: item.amount,
            startYear: item.startYear,
            startMonth: item.startMonth,
            endYear: item.endYear,
            endMonth: item.endMonth,
            isEdited: false,
            incomeType: {
              key: item.incomeType.key,
            },
          };
        })
      );
      setIsPageChanging(false);
      setTab(ASSETS_STEP);
      return;
    }

    if (tab === 3 && insurances.length > 0) {
      if (!(await saveInsurances())) return;
    }

    if (tab === 4) {
      if (!(await saveTaxes())) return;
      evaluateFilingStatus(
        advisorId,
        clientId,
        planId,
        taxes.taxFilingStatusType
      ).then((taxFilingResult) => {
        if (taxFilingResult.displayTaxFilingChangeUponDeath) {
          setIsPageChanging(false);
          setClientDeathYear(taxFilingResult.deathOfSpouseYear);
          setTab(TAX_FILING_STATUS_STEP);
        } else {
          setIsPageChanging(false);
          setTab(PLAN_NAME_STEP);
        }
      });
      return;
    }

    if (tab === TAX_FILING_STATUS_STEP) {
      if (!isTaxFilingItemizedDeductionValid()) {
        setIsPageChanging(false);
        return;
      }
      await updateTaxFilingStatusUponDeath();
      setIsPageChanging(false);
      setTab(PLAN_NAME_STEP);
      return;
    }

    if (tab === PLAN_NAME_STEP) {
      if (planName === "") {
        setIsPageChanging(false);
        return;
      }

      evaluateEarlyDistribution(advisorId, clientId, planId).then(
        (response) => {
          if (
            response.earlyDistribution ||
            response.earlyNoPenaltyAssetDistribution
          ) {
            savePlanName();
            setDistributionWarning(response);
          } else {
            savePlanName();
            onSavePlan(planId);
          }
        }
      );
      return;
    }

    if (tab === ASSETS_STEP) {
      if (!validateAssetsStep()) {
        setIsPageChanging(false);
        return;
      }
      if (
        checkRMDAssetsAvailable() &&
        !state.hasRMD &&
        !state.needDistributionSort
      ) {
        setState({
          ...state,
          hasRMD: true,
          needDistributionSort: false,
        });
        setIsPageChanging(false);
        return;
      }
      if (!validateRMDAssets()) {
        setIsPageChanging(false);
        return;
      }
      if (assets.length > 1 && !state.needDistributionSort) {
        setState({
          ...state,
          hasRMD: false,
          needDistributionSort: true,
        });
        setIsPageChanging(false);
        return;
      }
      if (!validateDistributionSortAssets()) {
        setIsPageChanging(false);
        return;
      }

      await saveAssets();
      setState({
        ...state,
        isNew: true,
        openCancelCreatePlanModal: false,
        hasRMD: false,
        needDistributionSort: false,
      });
    }

    setIsPageChanging(false);
    setTab((prevTab) => prevTab + 1);
  };

  const validateAssetsStep = () => {
    if (assets.length === 0) {
      setShowNoAssetsMessage(true);
      return false;
    }

    for (let index = 0; index < assets.length; index++) {
      const ast = assets[index];
      if (
        parseFloat(ast.preRetirementAllocation) < 0 ||
        parseFloat(ast.preRetirementAllocation) > 100 ||
        parseFloat(ast.postRetirementAllocation) < 0 ||
        parseFloat(ast.postRetirementAllocation) > 100
      ) {
        return false;
      }
      if (
        !checkDateRangeValid(
          ast.startYear,
          ast.startMonth,
          ast.endYear,
          ast.endMonth
        )
      ) {
        return false;
      }
    }

    return true;
  };

  const checkRMDAssetsAvailable = () => {
    return (
      assets.filter(
        (asset) => RMD_ACCOUNT_TYPES.indexOf(asset.accountType.key) > -1
      ).length > 0
    );
  };

  const validateRMDAssets = () => {
    const rmdAssets = assets.filter(
      (asset) => RMD_ACCOUNT_TYPES.indexOf(asset.accountType.key) > -1
    );
    const selectedRMDAssets = rmdAssets.filter((asset) => asset.isRMDEnable);
    if (rmdAssets.length === 0) {
      return true;
    } else {
      return selectedRMDAssets.length > 0;
    }
  };

  const validateDistributionSortAssets = () => {
    if (assets.length <= 1) return true;
    return (
      assets.filter(
        (asset) =>
          !Number.isInteger(asset.sortOrder) ||
          asset.sortOrder < 1 ||
          asset.sortOrder > assets.length
      ).length === 0 &&
      assets
        .map((asset) => {
          return asset.sortOrder;
        })
        .filter((sortOrder, index, self) => {
          return self.indexOf(sortOrder) == index;
        }).length === assets.length
    );
  };

  const goToPreviousStep = () => {
    if (tab === SOCIAL_SECURITY_BENEFITS_STEP) {
      setTab(INCOMES_STEP);
      return;
    }

    setTab((prevTab) => prevTab - 1);
  };

  const updateExpenses = (expenses) => {
    setExpenses(expenses);
  };

  const checkExpensesValid = (expenses) => {
    const editedExpenses = expenses.filter(
      (item) => parseFloat(item.amount) > 0
    );
    if (editedExpenses.length === 0) return false;

    for (const exp of editedExpenses) {
      if (!exp.name || exp.amount === "0") {
        return false;
      }
      if (
        !checkDateRangeValid(
          exp.startYear,
          exp.startMonth,
          exp.endYear,
          exp.endMonth
        )
      ) {
        console.log("YOU SHALL NOT PASS!");
        console.log(
          exp.name,
          exp.startYear,
          exp.startMonth,
          exp.endYear,
          exp.endMonth
        );

        return false;
      }
    }
    return true;
  };

  const checkIncomesValid = (incomes) => {
    const editedIncomes = incomes.filter((item) => parseFloat(item.amount) > 0);

    for (const inc of editedIncomes) {
      if (!inc.name || inc.amount === "0") {
        return false;
      }
      if (
        !checkDateRangeValid(
          inc.startYear,
          inc.startMonth,
          inc.endYear,
          inc.endMonth
        )
      ) {
        console.log("YOU SHALL NOT PASS!");
        console.log(
          inc.name,
          inc.startYear,
          inc.startMonth,
          inc.endYear,
          inc.endMonth
        );
        return false;
      }
    }
    return true;
  };

  const checkInsurancesValid = (insurances) => {
    for (const ins of insurances) {
      if (
        ins.appliesTo === null ||
        ins.policyType === null ||
        ins.deathBenefit === 0
      ) {
        return false;
      }
    }
    return true;
  };

  const checkTaxesValid = (taxes) => {
    const validateTaxOptionDate = (taxOption) => {
      return taxOption.date.startYear <= taxOption.date.endYear;
    };
    const taxCreditOptionValid = validateTaxOptionDate(
      taxes.planWizardTaxCreditViewList[0]
    );
    const capitalGainsOptionValid = validateTaxOptionDate(
      taxes.planWizardCapitalGainsViewList[0]
    );
    const propertyTaxesOptionValid = validateTaxOptionDate(
      taxes.planPropertyTaxViewList[0]
    );
    const taxDeductionEntry = taxDeductions.find(
      (item) => item.taxFilingStatusKey === taxes.taxFilingStatusType
    );
    const isDeductionAmountValid =
      taxes.deductionType === "ITEMIZED"
        ? taxes.itemizedDeductionAmount >= taxDeductionEntry.itemizedDeduction
        : true;

    return (
      taxCreditOptionValid &&
      capitalGainsOptionValid &&
      propertyTaxesOptionValid &&
      isDeductionAmountValid
    );
  };

  const changed = () => {
    setState({
      ...state,
      isNew: false,
    });
  };

  const deleteExpense = (item) => {
    return new Promise((resolve, reject) => {
      if (item.id === null) {
        resolve({});
        return;
      }
      deleteExpenseList(advisorId, clientId, planId, [item.id]).then(() =>
        resolve({})
      );
    });
  };

  const deleteIncome = (item) => {
    return new Promise((resolve, reject) => {
      if (item.id === null) {
        resolve({});
        return;
      }
      deletePlanIncome(advisorId, clientId, planId, [item.id]).then(() =>
        resolve({})
      );
    });
  };

  const deleteAssets = (item) => {
    return new Promise((resolve, reject) => {
      if (item.id === null) {
        resolve({});
        return;
      }

      deleteAssetList(advisorId, clientId, planId, [item.id]).then(() =>
        resolve({})
      );
    });
  };

  const deleteInsurance = (item) => {
    return new Promise((resolve, reject) => {
      if (item.id === null) {
        resolve({});
        return;
      }

      deletePlanInsurancePolicy(advisorId, clientId, planId, [
        item.id,
      ]).then(() => resolve({}));
    });
  };

  const shouldHideDeathOfSpouse = () => {
    if (coClient.id === undefined) return true;

    const clientLifeExpectancy = getLifeExpectancyDate(client);
    const coClientLifeExpectancy = getLifeExpectancyDate(coClient);

    return (
      clientLifeExpectancy.getFullYear() ===
      coClientLifeExpectancy.getFullYear()
    );
  };

  const renderTab = () => {
    switch (tab) {
      case EXPENSES_STEP:
        return (
          <ExpensesTab
            updateExpenses={updateExpenses}
            deleteItem={deleteExpense}
            data={expenses}
            changed={changed}
            hideBeforeRetirement={clientAge >= client.retirementAge}
            hideDeathOfSpouse={shouldHideDeathOfSpouse()}
          />
        );
      case INCOMES_STEP:
        return (
          <IncomesTab
            updateIncomes={setIncomes}
            deleteItem={deleteIncome}
            incomes={incomes}
            changed={changed}
            hideBeforeRetirement={clientAge >= client.retirementAge}
            hideDeathOfSpouse={shouldHideDeathOfSpouse()}
          />
        );
      case ASSETS_STEP:
        return (
          <AssetsTab
            assets={assets}
            deleteItem={deleteAssets}
            updateAssets={setAssets}
            defaultInflation={defaultInflation}
            changed={changed}
            hasRMD={state.hasRMD}
            needDistributionSort={state.needDistributionSort}
          />
        );
      case 3:
        return (
          <InsuranceTab
            updateInsurances={setInsurances}
            deleteItem={deleteInsurance}
            clearInsurances={() => {
              const insuranceIdList = insurances
                .filter((item) => item.id !== null)
                .map((item) => item.id);
              if (insuranceIdList.length > 0) {
                deletePlanInsurancePolicy(
                  advisorId,
                  clientId,
                  planId,
                  insuranceIdList
                ).then(() => {
                  setInsurances([]);
                });
              } else {
                setInsurances([]);
                return;
              }
            }}
            data={insurances}
            changed={changed}
          />
        );
      case TAXES_STEP:
        return (
          <TaxesTab
            taxes={taxes}
            updateTaxes={setTaxes}
            defaultTaxCredits={defaultTaxCredits}
            clientState={clientState}
            data={taxes}
            remainingYears={
              parseInt(lifeExpectancyYear.getFullYear()) -
              parseInt(getCurrentYear()) +
              1
            }
            taxDeductions={taxDeductions}
            changed={changed}
            isDarkTheme={isDarkTheme}
          />
        );
      case TAX_FILING_STATUS_STEP:
        return (
          <TaxFilingStatusUponDeathStep
            showValidationMessage={!isTaxFilingItemizedDeductionValid()}
            state={taxFilingUponDeath}
            setState={setTaxFilingUponDeath}
            deathYear={clientDeathYear}
          />
        );
      case PLAN_NAME_STEP:
        return <PlanNameTab planName={planName} setPlanName={setPlanName} />;
      case SOCIAL_SECURITY_BENEFITS_STEP:
        return (
          <SocialSecuritySurvivorBenefits
            socialSecuritySurvivorBenefits={socialSecuritySurvivorBenefits}
            setSocialSecuritySurvivorBeneifts={
              setSocialSecuritySurvivorBenefits
            }
            setHasSocialSecuritySurvivorBenefits={
              setHasSocialSecuritySurvivorBenefits
            }
          />
        );
      default:
        return null;
    }
  };

  let cancelCreatePlanModal;
  if (state.openCancelCreatePlanModal) {
    cancelCreatePlanModal = (
      <ConfirmationDialog
        title={"Confirmation"}
        message={
          "There is unsaved data on this page. Are you sure you want to leave this page?"
        }
        cancelLabel={"No"}
        confirmLabel={"Yes"}
        onCancel={() => {
          setState({
            ...state,
            openCancelCreatePlanModal: false,
          });
        }}
        onConfirm={() => {
          onClose();
          history.push(`/advisor/clients/details/${clientId}/plans`);
        }}
      />
    );
  }

  const noAssetsModal = (
    <ConfirmationDialog
      title={"Warning"}
      message={"Please provide at least one asset in order to continue."}
      hideCancel={true}
      cancelLabel={"No"}
      confirmLabel={"OK"}
      onCancel={() => {}}
      onConfirm={() => {
        setShowNoAssetsMessage(false);
      }}
    />
  );

  return (
    <Dialog
      id="plan-creation-modal"
      className={isDarkTheme ? "dark-theme" : ""}
      maxWidth="lg"
      fullWidth
      {...props}
      disableEscapeKeyDown={false}
      onClose={(event, reason) => {
        if (reason === "backdropClick") return;

        setState({
          ...state,
          openCancelCreatePlanModal: true,
        });
      }}
    >
      <Grid container justifyContent="space-between">
        <Grid item>
          <IconButton
            color="inherit"
            onClick={() => {
              setState({
                ...state,
                openCancelCreatePlanModal: true,
              });
            }}
            className="close-button"
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </Grid>
        <Grid item className="vestevo-logo">
          <img
            className="logo-img"
            src={isDarkTheme ? VestivoLogoDarkTheme : VestivoLogo}
            alt="Vestivo Logo"
          />
        </Grid>
        <Grid item className="plan-settings">
          <Button className="plan-settings-button" onClick={goToPlanSettings}>
            Plan settings&nbsp;&nbsp;
            <SettingsIcon />
          </Button>
          <Typography className="plan-settings-text">
            {tab !== 3 && (
              <React.Fragment>
                Expected inflation: {inflationRate}%,
                <br />
              </React.Fragment>
            )}
            Current age {clientAge} years,
            <br />
            Retirement Age {client.retirementAge} years, and
            <br />
            Life Expectancy {client.lifeExpectancyAge} years.
          </Typography>
        </Grid>
      </Grid>
      <Grid
        container
        className="subtitle-wrapper"
        md={9}
        direction="column"
        alignItems="center"
      >
        <Grid item>
          <Typography className="subtitle" component="span">
            Creating a plan for
          </Typography>
          <Button className="user-name-button" onClick={toggleUserDetails}>
            {client.firstName} {client.lastName}
            {isUserDetailsVisible ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Button>
        </Grid>
        {isUserDetailsVisible && (
          <>
            <Grid item className="user-details-wrapper">
              <Grid container spacing={6} justifyContent="center">
                <Grid item className="user-details-item">
                  <Typography className="user-details-field">
                    Status:
                  </Typography>
                  <Typography className="user-details-value">
                    {client.status.value || "-"}
                  </Typography>
                </Grid>
                <Grid item className="user-details-item">
                  <Typography className="user-details-field">
                    Birth date:
                  </Typography>
                  <Typography className="user-details-value">
                    {client.dateOfBirth} - {clientAge} Years Old
                  </Typography>
                </Grid>
              </Grid>
              <Grid container spacing={6} justifyContent="center">
                <Grid item className="user-details-item">
                  <Typography className="user-details-field">Phone:</Typography>
                  <Typography className="user-details-value">
                    {clientPhone}
                  </Typography>
                </Grid>
                <Grid item className="user-details-item">
                  <Typography className="user-details-field">
                    E-mail:
                  </Typography>
                  <Typography className="user-details-value">
                    {clientEmail}
                  </Typography>
                </Grid>
                <Grid item className="user-details-item">
                  <Typography className="user-details-field">
                    Address:
                  </Typography>
                  <Typography className="user-details-value">
                    {clientAddress}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Divider className="full-width" />
          </>
        )}
        <Grid item>
          <Tabs currentTab={tab} onSelectTab={setTab} />
        </Grid>
      </Grid>
      {isLoading && (
        <Grid item md={12}>
          <Loader />
        </Grid>
      )}
      {!isLoading && renderTab()}
      <Grid container md={9} justifyContent="flex-end">
        <Divider className="full-width" />
        <Grid item xs={6}>
          {tab > 0 && (
            <Button
              variant="contained"
              className={"left"}
              onClick={() => {
                goToPreviousStep();
              }}
            >
              <ArrowLeft />
              &nbsp;Back
            </Button>
          )}
        </Grid>
        <Grid item xs={6}>
          <Button
            variant="contained"
            className={"right"}
            onClick={goToNextStep}
          >
            Next&nbsp;
            <ArrowRight />
          </Button>
        </Grid>
      </Grid>
      {showNoAssetsMessage && noAssetsModal}
      {cancelCreatePlanModal}
      <AssetDistributionWarningModal
        distribution={distributionWarning}
        close={() => {
          setDistributionWarning({
            ...distributionWarning,
            earlyDistribution: false,
            earlyDistributionYear: null,
            earlyNoPenaltyAssetDistribution: false,
            earlyNoPenaltyAssetDistributionYear: null,
          });
        }}
        goToExpenseTab={() => {
          setTab(0);
          setIsPageChanging(false);
          setDistributionWarning({
            ...distributionWarning,
            earlyDistribution: false,
            earlyDistributionYear: null,
            earlyNoPenaltyAssetDistribution: false,
            earlyNoPenaltyAssetDistributionYear: null,
          });
        }}
        goToIncomeTab={() => {
          setTab(1);
          setIsPageChanging(false);
          setDistributionWarning({
            ...distributionWarning,
            earlyDistribution: false,
            earlyDistributionYear: null,
            earlyNoPenaltyAssetDistribution: false,
            earlyNoPenaltyAssetDistributionYear: null,
          });
        }}
        goToAssetsTab={() => {
          setIsPageChanging(false);
          setState({
            ...state,
            needDistributionSort: true,
          });
          setTab(ASSETS_STEP);
          setDistributionWarning({
            ...distributionWarning,
            earlyDistribution: false,
            earlyDistributionYear: null,
            earlyNoPenaltyAssetDistribution: false,
            earlyNoPenaltyAssetDistributionYear: null,
          });
        }}
        continueWithCalc={() => {
          onSavePlan(planId);
        }}
      />
    </Dialog>
  );
};

const mapStateToProps = (state) => {
  return {
    currentUser: state.currentUserReducer,
    currentClientState: state.currentClientReducer,
    defaultInflation: state.settingsReducer.getIn([
      "admin",
      "defaultInflation",
    ]),
    displayMode: state.uxStateReducer.getIn(["appearance", "displayMode"]),
    planCreationId: state.uxStateReducer.getIn(["planCreationId"]),
  };
};

const mapDispatchToProps = (dispatch) => {
  return {};
};

export default connect(mapStateToProps, mapDispatchToProps)(PlanCreationModal);
